import { useEffect, useState, useCallback } from "react";
import { PackageCheck, ChevronDown, ChevronUp, Truck } from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import api from "../../../lib/api";
import ReceiveOrderDialog from "../ReceiveOrderDialog";

const RECEIVABLE_STATUSES = new Set(["sent", "pending", "confirmed", "draft"]);

const STATUS_LABEL = {
  sent: "Enviado",
  pending: "Pendiente",
  confirmed: "Confirmado",
  draft: "Borrador",
};

/**
 * Banner que lista pedidos pendientes de recepción (status sent/pending/confirmed).
 * Cuando el usuario pulsa "Recibir", abre el diálogo estructurado para registrar
 * cantidades recibidas + incidencias por ítem.
 *
 * Aparece sólo si hay órdenes recepcionables → cero ruido cuando no hay nada.
 */
export default function PendingReceiveBanner({ restaurantId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [openOrder, setOpenOrder] = useState(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const all = await api.getOrders(restaurantId);
      const filtered = (Array.isArray(all) ? all : [])
        .filter(
          (o) =>
            RECEIVABLE_STATUSES.has(o.status) && o.source !== "auto_reorder",
        )
        .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setOrders(filtered);
    } catch {
      // silent — banner is optional
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSuccess = () => {
    setOpenOrder(null);
    load();
  };

  if (loading || orders.length === 0) return null;

  const totalAmount = orders.reduce((s, o) => s + (o.total_amount || 0), 0);

  return (
    <>
      <div
        className="border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 rounded-xl overflow-hidden"
        data-testid="pending-receive-banner"
      >
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-emerald-100/50 transition-colors"
          data-testid="pending-receive-toggle"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
              <Truck className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">
                {orders.length} pedido{orders.length === 1 ? "" : "s"} pendiente{orders.length === 1 ? "" : "s"} de recibir
              </p>
              <p className="text-xs text-emerald-700 dark:text-emerald-300">
                {totalAmount.toFixed(2)}€ en total · registra cantidades recibidas e incidencias al recibir la mercancía
              </p>
            </div>
          </div>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-emerald-700 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-emerald-700 flex-shrink-0" />
          )}
        </button>

        {expanded && (
          <div className="border-t border-emerald-200 bg-card dark:bg-background divide-y">
            {orders.map((order) => {
              const oid = order.order_id;
              return (
                <div
                  key={oid}
                  className="p-4 flex items-start justify-between gap-3"
                  data-testid={`pending-receive-row-${oid}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">
                        {order.supplier_name || "Proveedor sin asignar"}
                      </p>
                      <Badge variant="outline" className="text-[10px]">
                        {STATUS_LABEL[order.status] || order.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        #{oid.slice(-8)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {(order.items || []).length} ítems ·{" "}
                      {(order.total_amount || 0).toFixed(2)}€
                      {order.sent_at && (
                        <span>
                          {" "}
                          · enviado {new Date(order.sent_at).toLocaleDateString("es-ES")}
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setOpenOrder(order)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
                    data-testid={`pending-receive-btn-${oid}`}
                  >
                    <PackageCheck className="w-3.5 h-3.5 mr-1.5" />
                    Recibir
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ReceiveOrderDialog
        open={!!openOrder}
        onOpenChange={(o) => { if (!o) setOpenOrder(null); }}
        order={openOrder}
        onSuccess={handleSuccess}
      />
    </>
  );
}
